import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'pt';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.features': 'Features',
    'nav.modules': 'Modules',
    'nav.module.management': 'Management Center',
    'nav.module.status': 'Status & Report',
    'nav.module.mobile': 'Mobile',
    'nav.module.production': 'Production',
    'nav.benefits': 'Benefits',
    'nav.technology': 'Technology',
    'nav.contact': 'Contact',
    
    // Hero Section
    'hero.title.main': 'Mozmina: Complete',
    'hero.title.highlight': 'Mining Operations Control',
    'hero.title.subtitle': 'System',
    'hero.description': 'Operational control and equipment management software for mining operations with real-time monitoring, comprehensive reporting, and intelligent fleet coordination.',
    'hero.button.start': 'Get Started',
    'hero.button.demo': 'Export PDF',
    'hero.stat.automatic': 'Automatic',
    'hero.stat.automatic.sub': 'Assignment System',
    'hero.stat.realtime': 'Real-time',
    'hero.stat.realtime.sub': 'Fleet Management',
    'hero.stat.production': '24/7',
    'hero.stat.production.sub': 'Production Tracking',

    // Features Section - 4 Official Modules
    'features.title': 'Mozmina System Modules',
    'features.description': 'Complete mining operations management through four integrated modules providing operational control, status monitoring, mobile coordination, and production intelligence.',
    'features.gallery.title': 'System Module Interfaces',
    'features.module1.title': 'Mozmina Management Center',
    'features.module1.description': 'Complete system configuration and operational structure setup - Operator registration, shift management, fleet setup, operational activities, and maintenance scheduling',
    'features.module2.title': 'Mozmina Status and Report',
    'features.module2.description': 'Equipment status monitoring and comprehensive reporting - Physical availability, utilization metrics, MTBF analysis, and operational dashboards',
    'features.module3.title': 'Mozmina Mobile',
    'features.module3.description': 'Embedded tablet interface for field equipment - Real-time data collection, GPS tracking, offline functionality with store-and-forward synchronization',
    'features.module4.title': 'Mozmina Production',
    'features.module4.description': 'Advanced production cycle management - Truck-excavator cycle tracking, automatic assignment system, KPI monitoring, and queue management',
    
    // Contact Section
    'contact.title': 'Get in Touch',
    'contact.subtitle': 'Ready to transform your mining operations? Contact us today.',
    'contact.form.title': 'Send us a message',
    'contact.form.name': 'Full Name',
    'contact.form.name.placeholder': 'John Doe',
    'contact.form.email': 'Email Address',
    'contact.form.email.placeholder': 'john@company.com',
    'contact.form.company': 'Company',
    'contact.form.company.placeholder': 'Mining Corp',
    'contact.form.message': 'Message',
    'contact.form.message.placeholder': 'Tell us about your needs...',
    'contact.form.submit': 'Send Message',
    'contact.info.title': 'Contact Information',
    'contact.info.phone': 'Phone',
    'contact.info.email': 'Email',
    'contact.info.office': 'Office',
    'contact.info.phone.value': 'Contact for details',
    'contact.hours.title': 'Business Hours',
    'contact.hours.weekdays': 'Monday - Friday: 8:00 AM - 6:00 PM',
    'contact.hours.saturday': 'Saturday: 9:00 AM - 4:00 PM',
    'contact.hours.sunday': 'Sunday: Emergency Support Only',
    'contact.follow.title': 'Follow Us',
    'contact.footer.privacy': 'Privacy Policy',
    'contact.footer.terms': 'Terms of Service',
    'contact.footer.cookies': 'Cookie Policy',
    'contact.footer.sitemap': 'Sitemap',
    'contact.footer.copyright': `© ${new Date().getFullYear()} Mozmina. All rights reserved.`,

    // Benefits Section (replacing Statistics)
    'benefits.title': 'Mozmina System Benefits',
    'benefits.description': 'Experience the transformational impact of comprehensive mining operations management with real-time monitoring, cost reduction, operational efficiency, and continuous improvement.',
    'benefits.realtime.title': 'Real-time Monitoring',
    'benefits.cost.title': 'Cost Reduction',
    'benefits.efficiency.title': 'Operational Efficiency',
    'benefits.improvement.title': 'Continuous Improvement',
    'benefits.impact.title': 'Why Choose Mozmina?',
    'benefits.impact.security.icon': '🛡️',
    'benefits.impact.security.text': 'Security Standards Compliance',
    'benefits.impact.assets.icon': '📊',
    'benefits.impact.assets.text': 'Effective Asset Management',
    'benefits.impact.decisions.icon': '🎯',
    'benefits.impact.decisions.text': 'Informed Decision Making',

    // Infrastructure Section (replacing Technology)
    'infrastructure.title': 'Cloud-Based Platform Technology',
    'infrastructure.description': 'Enterprise-grade Google Cloud Platform with robust security, offline capabilities for mobile network issues, and PostgreSQL database for reliable mining operations management.',
    'infrastructure.hardware.title': 'Google Cloud Platform',
    'infrastructure.hardware.description': 'Enterprise-grade cloud infrastructure with 99.9% uptime',
    'infrastructure.hardware.category': 'Cloud',
    'infrastructure.server.title': 'Security & Compliance',
    'infrastructure.server.description': 'End-to-end encryption, secure authentication, and data protection',
    'infrastructure.server.category': 'Security',
    'infrastructure.communication.title': 'Offline Capabilities',
    'infrastructure.communication.description': 'Mobile apps work offline during network issues with auto-sync',
    'infrastructure.communication.category': 'Resilience',
    'infrastructure.mobile.title': 'PostgreSQL Database',
    'infrastructure.mobile.description': 'High-performance database with automatic backups and scaling',
    'infrastructure.mobile.category': 'Database',
    'infrastructure.onpremise.title': 'On-Premise Deployment',
    'infrastructure.onpremise.description': 'Complete local deployment option for enhanced security and control',
    'infrastructure.onpremise.category': 'Deployment',
    'infrastructure.ai.title': 'AI Integration Ready',
    'infrastructure.ai.description': 'Advanced analytics, KPI optimization, and dynamic assignment algorithms',
    'infrastructure.ai.category': 'AI/ML',
    'infrastructure.platform.title': 'Google Cloud Excellence',
    'infrastructure.platform.description': 'Built on Google Cloud Platform providing enterprise-grade reliability, security, and scalability for mining operations worldwide.',
    'infrastructure.platform.feature1': '☁️ Google Cloud Infrastructure: Scalable cloud platform with global reach',
    'infrastructure.platform.feature2': '🛡️ Enterprise Security: Multi-layer security with Google Cloud Identity',
    'infrastructure.platform.feature3': '📱 Offline Resilience: Mobile apps continue working during network outages',
    'infrastructure.platform.feature4': '🗄️ PostgreSQL Database: Reliable data storage with automatic backups',
    'infrastructure.capabilities.title': 'Technology Capabilities',
    'infrastructure.capabilities.security': '🔐 Google Cloud Security Command Center Integration',
    'infrastructure.capabilities.assets': '☁️ Cloud-Native Architecture',
    'infrastructure.capabilities.decisions': '📡 Offline-First Mobile Design',
    'infrastructure.capabilities.integration': '🗄️ PostgreSQL Enterprise Database',

    // Module Details
    'modules.management.features.title': 'Key Features',
    'modules.management.features.operators': 'Operator Registration and Management',
    'modules.management.features.shifts': 'Shift and Crew Management',
    'modules.management.features.fleets': 'Fleet Configuration and Setup',
    'modules.management.features.activities': 'Equipment Operational Activities',
    'modules.management.features.maintenance': 'Maintenance Scheduling and Tracking',
    'modules.management.features.operations': 'Operational Stop Reasons',

    'modules.status.features.title': 'Monitoring Capabilities',
    'modules.status.features.equipment': 'Real-time Equipment Status',
    'modules.status.features.availability': 'Physical Availability Metrics',
    'modules.status.features.utilization': 'Equipment Utilization Analysis',
    'modules.status.features.mtbf': 'MTBF (Mean Time Between Failures)',
    'modules.status.features.worked': 'Worked Hours by Activity Measurement',
    'modules.status.features.operational': 'Operational Stop Time Measurement',
    'modules.status.features.maintenance': 'Maintenance Stop Time Measurement',

    'modules.mobile.features.title': 'Mobile Capabilities',
    'modules.mobile.features.integrated': 'Integrated with all Mozmina modules',
    'modules.mobile.features.interaction': 'Interactive configuration parameter modification',
    'modules.mobile.features.realtime': 'Real-time parameter updates',
    'modules.mobile.features.offline': 'Store and forward system (offline capable)',
    'modules.mobile.features.gps': 'GPS location tracking and coordination',
    'modules.mobile.features.intuitive': 'Intuitive interface for field operations',

    'modules.production.features.title': 'Production Intelligence',
    'modules.production.features.cycle': 'Complete truck-excavator cycle tracking',
    'modules.production.features.assignment': 'Automatic equipment assignment system',
    'modules.production.features.kpis': 'Real-time KPI monitoring and calculation',
    'modules.production.features.queue': 'FIFO queue management with manual override',
    'modules.production.features.tracking': 'Time and distance tracking for all cycles',
    'modules.production.features.realtime': 'Live production dashboard updates',
    'modules.production.gallery.title': 'Production Interfaces',
    'modules.production.flow.title': 'Production Cycle States',
    'modules.production.flow.empty': 'Empty Travel',
    'modules.production.flow.empty.desc': 'Truck traveling empty to excavator location',
    'modules.production.flow.loading': 'Loading',
    'modules.production.flow.loading.desc': 'Truck being loaded by excavator (queue management)',
    'modules.production.flow.full': 'Full Travel',
    'modules.production.flow.full.desc': 'Truck traveling full to assigned dump location',
    'modules.production.flow.dumping': 'Dumping',
    'modules.production.flow.dumping.desc': 'Truck dumping at location and completing cycle',
    'modules.production.excavator.title': 'Excavator Control Interface',
    'modules.production.excavator.desc': 'Control Room Actions: "Início de Carga" (Start Loading) when truck arrives, "Cheio" (Full) when loading complete. System tracks excavator hang time and manages FIFO queue with manual override capability.',
    'modules.production.truck.title': 'Truck Action Interface',
    'modules.production.truck.desc': 'Control Room Actions: "Chegada" (Arrival) at excavator/dump locations, "Finalizar" (Complete) when dumping finished. Tracks travel times, queue positions, and automatically assigns dump locations.',
    'modules.production.kpi.title': 'Complete Production KPI Monitoring',
    'modules.production.kpi.emphasis': '🎯 Mozmina manages the entire production cycle automatically - from truck assignment to cycle completion with comprehensive KPI tracking',
    'modules.production.kpi.truck.title': 'Truck Performance KPIs',
    'modules.production.kpi.operations.title': 'Operational Efficiency KPIs',
    'modules.production.kpi.analytics.title': 'Production Analytics',
    'modules.production.kpi.empty.travel': 'Empty Travel Time',
    'modules.production.kpi.empty.travel.desc': 'Time from dispatch to excavator arrival',
    'modules.production.kpi.full.travel': 'Full Travel Time',
    'modules.production.kpi.full.travel.desc': 'Time from loading completion to dump arrival',
    'modules.production.kpi.empty.distance': 'Empty Distance',
    'modules.production.kpi.empty.distance.desc': 'Distance traveled empty to excavator',
    'modules.production.kpi.full.distance': 'Full Distance',
    'modules.production.kpi.full.distance.desc': 'Distance traveled full to dump location',
    'modules.production.kpi.cycle.time': 'Complete Cycle Time',
    'modules.production.kpi.cycle.time.desc': 'Total time from assignment to completion',
    'modules.production.kpi.spot.time': 'Spot Time',
    'modules.production.kpi.spot.time.desc': 'Wait time from arrival to loading start',
    'modules.production.kpi.queue.time': 'Queue Time',
    'modules.production.kpi.queue.time.desc': 'Loading and dumping queue delays',
    'modules.production.kpi.loading.time': 'Loading Time',
    'modules.production.kpi.loading.time.desc': 'Active excavator loading duration',
    'modules.production.kpi.dumping.time': 'Dumping Time',
    'modules.production.kpi.dumping.time.desc': 'Active dumping operation duration',
    'modules.production.kpi.hang.time': 'Excavator Hang Time',
    'modules.production.kpi.hang.time.desc': 'Time excavator waits for truck arrival',
    'modules.production.kpi.total.production': 'Total Production',
    'modules.production.kpi.total.production.desc': 'Daily/shift material movement totals',
    'modules.production.kpi.equipment.utilization': 'Equipment Utilization',
    'modules.production.kpi.equipment.utilization.desc': 'Truck and excavator efficiency rates',
    'modules.production.kpi.cycle.efficiency': 'Cycle Efficiency',
    'modules.production.kpi.cycle.efficiency.desc': 'Overall production cycle optimization',
    'modules.production.kpi.fleet.performance': 'Fleet Performance',
    'modules.production.kpi.fleet.performance.desc': 'Comparative analysis across equipment',
    'modules.production.kpi.realtime.monitoring': 'Real-time Monitoring',
    'modules.production.kpi.realtime.monitoring.desc': 'Live dashboard updates and alerts'
  },
  pt: {
    // Navigation
    'nav.home': 'Início',
    'nav.features': 'Recursos',
    'nav.modules': 'Módulos',
    'nav.module.management': 'Management Center',
    'nav.module.status': 'Status & Report',
    'nav.module.mobile': 'Mobile',
    'nav.module.production': 'Production',
    'nav.benefits': 'Benefícios',
    'nav.technology': 'Tecnologia',
    'nav.contact': 'Contato',
    
    // Hero Section
    'hero.title.main': 'Mozmina: Sistema Completo',
    'hero.title.highlight': 'de Controlo de Operações',
    'hero.title.subtitle': 'Mineiras',
    'hero.description': 'Software de controlo operacional e gestão de equipamentos aplicado à mineração com monitorização em tempo real, relatórios abrangentes e coordenação inteligente da frota.',
    'hero.button.start': 'Começar',
    'hero.button.demo': 'Exportar PDF',
    'hero.stat.automatic': 'Sistema',
    'hero.stat.automatic.sub': 'Atribuição Automática',
    'hero.stat.realtime': 'Tempo Real',
    'hero.stat.realtime.sub': 'Gestão de Frota',
    'hero.stat.production': '24/7',
    'hero.stat.production.sub': 'Acompanhamento de Produção',

    // Features Section - 4 Official Modules
    'features.title': 'Módulos do Sistema Mozmina',
    'features.description': 'Gestão completa de operações mineiras através de quatro módulos integrados fornecendo controlo operacional, monitorização de estado, coordenação móvel e inteligência de produção.',
    'features.gallery.title': 'Interfaces dos Módulos do Sistema',
    'features.module1.title': 'Mozmina Management Center',
    'features.module1.description': 'Configuração completa do sistema e estrutura operacional - Registo de operadores, gestão de turnos, configuração de frotas, atividades operacionais e programação de manutenção',
    'features.module2.title': 'Mozmina Status and Report',
    'features.module2.description': 'Monitorização de estado de equipamentos e relatórios abrangentes - Disponibilidade física, métricas de utilização, análise MTBF e painéis operacionais',
    'features.module3.title': 'Mozmina Mobile',
    'features.module3.description': 'Interface de tablet embarcado para equipamentos de campo - Recolha de dados em tempo real, rastreio GPS, funcionalidade offline com sincronização store-and-forward',
    'features.module4.title': 'Mozmina Production',
    'features.module4.description': 'Gestão avançada de ciclos de produção - Rastreio de ciclos camião-escavadora, sistema de atribuição automática, monitorização de KPIs e gestão de filas',
    
    // Contact Section
    'contact.title': 'Entre em Contacto',
    'contact.subtitle': 'Pronto para transformar as suas operações mineiras? Entre em contacto connosco hoje.',
    'contact.form.title': 'Envie-nos uma mensagem',
    'contact.form.name': 'Nome Completo',
    'contact.form.name.placeholder': 'João Silva',
    'contact.form.email': 'Endereço de Email',
    'contact.form.email.placeholder': 'joao@empresa.pt',
    'contact.form.company': 'Empresa',
    'contact.form.company.placeholder': 'Mineração Corp',
    'contact.form.message': 'Mensagem',
    'contact.form.message.placeholder': 'Fale-nos sobre as suas necessidades...',
    'contact.form.submit': 'Enviar Mensagem',
    'contact.info.title': 'Informações de Contacto',
    'contact.info.phone': 'Telefone',
    'contact.info.email': 'Email',
    'contact.info.office': 'Escritório',
    'contact.info.phone.value': 'Entre em contacto para detalhes',
    'contact.hours.title': 'Horário Comercial',
    'contact.hours.weekdays': 'Segunda - Sexta: 8:00 - 18:00',
    'contact.hours.saturday': 'Sábado: 9:00 - 16:00',
    'contact.hours.sunday': 'Domingo: Apenas Suporte Emergencial',
    'contact.follow.title': 'Siga-nos',
    'contact.footer.privacy': 'Política de Privacidade',
    'contact.footer.terms': 'Termos de Serviço',
    'contact.footer.cookies': 'Política de Cookies',
    'contact.footer.sitemap': 'Mapa do Site',
    'contact.footer.copyright': `© ${new Date().getFullYear()} Mozmina. Todos os direitos reservados.`,

    // Benefits Section (replacing Statistics)
    'benefits.title': 'Benefícios do Sistema Mozmina',
    'benefits.description': 'Experimente o impacto transformacional da gestão abrangente de operações mineiras com monitorização em tempo real, redução de custos, eficiência operacional e melhoria contínua.',
    'benefits.realtime.title': 'Monitorização em Tempo Real',
    'benefits.cost.title': 'Redução de Custos',
    'benefits.efficiency.title': 'Eficiência Operacional',
    'benefits.improvement.title': 'Melhoria Contínua',
    'benefits.impact.title': 'Porquê escolher a Mozmina?',
    'benefits.impact.security.icon': '🛡️',
    'benefits.impact.security.text': 'Conformidade com Padrões de Segurança',
    'benefits.impact.assets.icon': '📊',
    'benefits.impact.assets.text': 'Gestão Eficaz de Activos',
    'benefits.impact.decisions.icon': '🎯',
    'benefits.impact.decisions.text': 'Tomada de Decisão Informada',

    // Infrastructure Section (replacing Technology)
    'infrastructure.title': 'Tecnologia de Plataforma Baseada em Cloud',
    'infrastructure.description': 'Plataforma Google Cloud de nível empresarial com segurança robusta, capacidades offline para problemas de rede móvel, e base de dados PostgreSQL para gestão confiável de operações minerárias.',
    'infrastructure.hardware.title': 'Plataforma Google Cloud',
    'infrastructure.hardware.description': 'Infraestrutura cloud de nível empresarial com 99.9% de uptime',
    'infrastructure.hardware.category': 'Cloud',
    'infrastructure.server.title': 'Segurança & Conformidade',
    'infrastructure.server.description': 'Encriptação end-to-end, autenticação segura e proteção de dados',
    'infrastructure.server.category': 'Segurança',
    'infrastructure.communication.title': 'Capacidades Offline',
    'infrastructure.communication.description': 'Aplicações móveis funcionam offline durante problemas de rede com sincronização automática',
    'infrastructure.communication.category': 'Resistência',
    'infrastructure.mobile.title': 'Base de Dados PostgreSQL',
    'infrastructure.mobile.description': 'Base de dados de alta performance com backups automáticos e escalabilidade',
    'infrastructure.mobile.category': 'Base de Dados',
    'infrastructure.onpremise.title': 'Implementação On-Premise',
    'infrastructure.onpremise.description': 'Opção completa de implementação local para maior segurança e controlo',
    'infrastructure.onpremise.category': 'Implementação',
    'infrastructure.ai.title': 'Integração AI Disponível',
    'infrastructure.ai.description': 'Análise avançada, optimização de KPIs e algoritmos de atribuição dinâmica',
    'infrastructure.ai.category': 'AI/ML',
    'infrastructure.platform.title': 'Excelência Google Cloud',
    'infrastructure.platform.description': 'Construído na Google Cloud Platform fornecendo confiabilidade, segurança e escalabilidade de nível empresarial para operações minerárias mundiais.',
    'infrastructure.platform.feature1': '☁️ Infraestrutura Google Cloud: Plataforma cloud escalável com alcance global',
    'infrastructure.platform.feature2': '🛡️ Segurança Empresarial: Segurança multi-camada com Google Cloud Identity',
    'infrastructure.platform.feature3': '📱 Resistência Offline: Aplicações móveis continuam a funcionar durante interrupções de rede',
    'infrastructure.platform.feature4': '🗄️ Base de Dados PostgreSQL: Armazenamento confiável com backups automáticos',
    'infrastructure.capabilities.title': 'Capacidades Tecnológicas',
    'infrastructure.capabilities.security': '🔐 Integração Google Cloud Security Command Center',
    'infrastructure.capabilities.assets': '☁️ Arquitetura Cloud-Native',
    'infrastructure.capabilities.decisions': '📡 Design Mobile Offline-First',
    'infrastructure.capabilities.integration': '🗄️ Base de Dados PostgreSQL Empresarial',

    // Module Details
    'modules.management.features.title': 'Recursos Principais',
    'modules.management.features.operators': 'Registo e Gestão de Operadores',
    'modules.management.features.shifts': 'Gestão de Turnos e Turmas',
    'modules.management.features.fleets': 'Configuração e Setup de Frotas',
    'modules.management.features.activities': 'Actividades Operacionais de Equipamentos',
    'modules.management.features.maintenance': 'Programação e Rastreio de Manutenção',
    'modules.management.features.operations': 'Motivos de Paragens Operacionais',

    'modules.status.features.title': 'Capacidades de Monitorização',
    'modules.status.features.equipment': 'Status de Equipamentos em Tempo Real',
    'modules.status.features.availability': 'Métricas de Disponibilidade Física',
    'modules.status.features.utilization': 'Análise de Utilização de Equipamentos',
    'modules.status.features.mtbf': 'MTBF (Tempo Médio Entre Falhas)',
    'modules.status.features.worked': 'Medição de Horas Trabalhadas por Atividade',
    'modules.status.features.operational': 'Medição de Tempo de Paragens Operacionais',
    'modules.status.features.maintenance': 'Medição de Tempo de Paragens de Manutenção',

    'modules.mobile.features.title': 'Capacidades Móveis',
    'modules.mobile.features.integrated': 'Integrado com todos os módulos Mozmina',
    'modules.mobile.features.interaction': 'Modificação interactiva de parâmetros de configuração',
    'modules.mobile.features.realtime': 'Actualizações de parâmetros em tempo real',
    'modules.mobile.features.offline': 'Sistema store and forward (funciona offline)',
    'modules.mobile.features.gps': 'Rastreio e coordenação de localização GPS',
    'modules.mobile.features.intuitive': 'Interface intuitiva para operações de campo',

    'modules.production.features.title': 'Inteligência de Produção',
    'modules.production.features.cycle': 'Rastreio completo de ciclos camião-escavadora',
    'modules.production.features.assignment': 'Sistema de atribuição automática de equipamentos',
    'modules.production.features.kpis': 'Monitorização e cálculo de KPIs em tempo real',
    'modules.production.features.queue': 'Gestão de fila FIFO com substituição manual',
    'modules.production.features.tracking': 'Rastreio de tempo e distância para todos os ciclos',
    'modules.production.features.realtime': 'Actualizações do painel de produção ao vivo',
    'modules.production.gallery.title': 'Interfaces de Produção',
    'modules.production.flow.title': 'Estados do Ciclo de Produção',
    'modules.production.flow.empty': 'Viagem Vazio',
    'modules.production.flow.empty.desc': 'Camião a viajar vazio para localização da escavadora',
    'modules.production.flow.loading': 'Carregamento',
    'modules.production.flow.loading.desc': 'Camião a ser carregado pela escavadora (gestão de fila)',
    'modules.production.flow.full': 'Viagem Cheio',
    'modules.production.flow.full.desc': 'Camião a viajar cheio para local de descarga atribuído',
    'modules.production.flow.dumping': 'Descarga',
    'modules.production.flow.dumping.desc': 'Camião a descarregar no local e a completar o ciclo',
    'modules.production.excavator.title': 'Interface de Controlo de Escavadora',
    'modules.production.excavator.desc': 'Ações da Sala de Controlo: "Início de Carga" quando o camião chega, "Cheio" quando o carregamento está completo. O sistema rastreia o tempo de espera da escavadora e gere a fila FIFO com capacidade de substituição manual.',
    'modules.production.truck.title': 'Interface de Ação do Camião',
    'modules.production.truck.desc': 'Ações da Sala de Controlo: "Chegada" nas localizações de escavadora/descarga, "Finalizar" quando a descarga termina. Rastreia tempos de viagem, posições na fila e atribui automaticamente locais de descarga.',
    'modules.production.kpi.title': 'Monitorização Completa de KPIs de Produção',
    'modules.production.kpi.emphasis': '🎯 Mozmina gere todo o ciclo de produção automaticamente - desde a atribuição do camião até à conclusão do ciclo com rastreio abrangente de KPIs',
    'modules.production.kpi.truck.title': 'KPIs de Desempenho do Camião',
    'modules.production.kpi.operations.title': 'KPIs de Eficiência Operacional',
    'modules.production.kpi.analytics.title': 'Análise de Produção',
    'modules.production.kpi.empty.travel': 'Tempo de Viagem Vazio',
    'modules.production.kpi.empty.travel.desc': 'Tempo desde o despacho até à chegada à escavadora',
    'modules.production.kpi.full.travel': 'Tempo de Viagem Cheio',
    'modules.production.kpi.full.travel.desc': 'Tempo desde a conclusão do carregamento até a chegada à descarga',
    'modules.production.kpi.empty.distance': 'Distância Vazio',
    'modules.production.kpi.empty.distance.desc': 'Distância percorrida vazio até à escavadora',
    'modules.production.kpi.full.distance': 'Distância Cheio',
    'modules.production.kpi.full.distance.desc': 'Distância percorrida cheio até o local de descarga',
    'modules.production.kpi.cycle.time': 'Tempo de Ciclo Completo',
    'modules.production.kpi.cycle.time.desc': 'Tempo total desde a atribuição até a conclusão',
    'modules.production.kpi.spot.time': 'Tempo de Posicionamento',
    'modules.production.kpi.spot.time.desc': 'Tempo de espera desde a chegada até o início do carregamento',
    'modules.production.kpi.queue.time': 'Tempo de Fila',
    'modules.production.kpi.queue.time.desc': 'Atrasos de fila de carregamento e descarga',
    'modules.production.kpi.loading.time': 'Tempo de Carregamento',
    'modules.production.kpi.loading.time.desc': 'Duração activa do carregamento pela escavadora',
    'modules.production.kpi.dumping.time': 'Tempo de Descarga',
    'modules.production.kpi.dumping.time.desc': 'Duração activa da operação de descarga',
    'modules.production.kpi.hang.time': 'Tempo de Espera da Escavadora',
    'modules.production.kpi.hang.time.desc': 'Tempo que a escavadora espera pela chegada do camião',
    'modules.production.kpi.total.production': 'Produção Total',
    'modules.production.kpi.total.production.desc': 'Totais de movimentação de material diário/por turno',
    'modules.production.kpi.equipment.utilization': 'Utilização de Equipamentos',
    'modules.production.kpi.equipment.utilization.desc': 'Taxas de eficiência de camiões e escavadoras',
    'modules.production.kpi.cycle.efficiency': 'Eficiência do Ciclo',
    'modules.production.kpi.cycle.efficiency.desc': 'Optimização geral do ciclo de produção',
    'modules.production.kpi.fleet.performance': 'Desempenho da Frota',
    'modules.production.kpi.fleet.performance.desc': 'Análise comparativa entre equipamentos',
    'modules.production.kpi.realtime.monitoring': 'Monitorização em Tempo Real',
    'modules.production.kpi.realtime.monitoring.desc': 'Actualizações e alertas do painel ao vivo'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('pt');

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};